import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Image as ImageIcon, ArrowRight, Loader2, Download,
  Sparkles, CheckCircle2, X, ChevronDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRANCHES = [
  { value: 'gastronomie', label: 'Gastronomie & Hotellerie' },
  { value: 'handwerk', label: 'Handwerk & Dienstleistung' },
  { value: 'fitness', label: 'Fitness & Gesundheit' },
  { value: 'detailhandel', label: 'Detailhandel & Gewerbe' },
  { value: 'events', label: 'Events & Veranstaltungen' },
  { value: 'gesundheit', label: 'Praxis & Wellness' },
  { value: 'sonstiges', label: 'Andere Branche' },
];

const COLORS = [
  { value: '', label: 'Automatisch', bg: 'bg-gradient-to-br from-gray-200 to-gray-300' },
  { value: 'red', label: 'Rot', bg: 'bg-red-500' },
  { value: 'blue', label: 'Blau', bg: 'bg-blue-500' },
  { value: 'green', label: 'Grün', bg: 'bg-green-500' },
  { value: 'yellow', label: 'Gelb', bg: 'bg-yellow-400' },
  { value: 'black', label: 'Schwarz', bg: 'bg-gray-900' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500' },
];

const MockupGeneratorPage = () => {
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [firmenname, setFirmenname] = useState('');
  const [branche, setBranche] = useState('sonstiges');
  const [werbetext, setWerbetext] = useState('');
  const [farbe, setFarbe] = useState('');
  const [generating, setGenerating] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [email, setEmail] = useState('');
  const [leadSaved, setLeadSaved] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Bitte laden Sie eine Bilddatei hoch (PNG, JPG)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Datei zu gross (max 10MB)');
      return;
    }
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
    setError(null);
    setResultUrl(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  }, [handleFileSelect]);

  const handleGenerate = async () => {
    if (!firmenname && !werbetext) {
      setError('Bitte geben Sie einen Firmennamen oder Werbetext ein');
      return;
    }
    setGenerating(true);
    setError(null);
    setResultUrl(null);

    const formData = new FormData();
    if (logo) formData.append('logo', logo);
    formData.append('firmenname', firmenname);
    formData.append('branche', branche);
    formData.append('werbetext', werbetext);
    formData.append('farbe', farbe);

    try {
      const res = await fetch(`${API_URL}/api/mockup/generate`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Generierung fehlgeschlagen');
      }
      const data = await res.json();
      setResultUrl(`${API_URL}${data.image_url}`);
    } catch (e) {
      setError(e.message || 'Fehler bei der Generierung');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveLead = async () => {
    if (!email || !email.includes('@')) return;
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('image_url', resultUrl || '');
      formData.append('branche', branche);
      await fetch(`${API_URL}/api/mockup/save-lead`, { method: 'POST', body: formData });
      setLeadSaved(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDownload = () => {
    if (!resultUrl) return;
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `mein-werbe-mockup.png`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="py-8 lg:py-16" data-testid="mockup-generator-page">
      <SEO
        title="Mockup-Generator – So sieht Ihre Werbung aus"
        description="Laden Sie Ihr Logo hoch und sehen Sie sofort, wie Ihre Werbung auf einem mobilen Werbeanhänger aussehen würde. Kostenlos und unverbindlich."
        canonicalPath="/mockup-generator"
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Kostenlos & unverbindlich
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4">
            So sieht <span className="text-gradient">Ihre Werbung</span> aus
          </h1>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
            Laden Sie Ihr Logo hoch, wählen Sie Ihre Branche – und unsere KI zeigt Ihnen in Sekunden, wie Ihre Werbung auf einem Anhänger wirkt.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Left: Form */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
            <Card className="card-shadow">
              <CardContent className="p-6 space-y-5">
                {/* Company Name */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">1. Ihr Firmenname *</Label>
                  <Input
                    placeholder='z.B. "Bäckerei Müller" oder "FitZone Wetzikon"'
                    value={firmenname}
                    onChange={(e) => setFirmenname(e.target.value)}
                    maxLength={50}
                    data-testid="firmenname-input"
                  />
                </div>

                {/* Branch */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">2. Ihre Branche</Label>
                  <div className="relative">
                    <select
                      value={branche}
                      onChange={(e) => setBranche(e.target.value)}
                      className="w-full h-10 rounded-md border border-[hsl(var(--border))] bg-white px-3 pr-8 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                      data-testid="branche-select"
                    >
                      {BRANCHES.map((b) => (
                        <option key={b.value} value={b.value}>{b.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-3 top-3 text-[hsl(var(--muted-foreground))] pointer-events-none" />
                  </div>
                </div>

                {/* Ad Text */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">3. Werbetext (optional)</Label>
                  <Input
                    placeholder='z.B. "Jetzt Probetraining!" oder "Frisch gebacken"'
                    value={werbetext}
                    onChange={(e) => setWerbetext(e.target.value)}
                    maxLength={60}
                    data-testid="werbetext-input"
                  />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{werbetext.length}/60 Zeichen</p>
                </div>

                {/* Logo Upload (optional) */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">4. Logo hochladen (optional)</Label>
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                      logoPreview
                        ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.03)]'
                        : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--surface-2))]'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    data-testid="logo-upload-area"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleFileSelect(e.target.files[0])}
                      data-testid="logo-file-input"
                    />
                    {logoPreview ? (
                      <div className="relative inline-block">
                        <img src={logoPreview} alt="Ihr Logo" className="max-h-16 max-w-full mx-auto rounded" />
                        <button
                          onClick={(e) => { e.stopPropagation(); setLogo(null); setLogoPreview(null); }}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Upload className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <span className="text-sm text-[hsl(var(--muted-foreground))]">Logo ablegen oder klicken</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Wird als Referenz für das Design verwendet</p>
                </div>

                {/* Color */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">5. Markenfarbe (optional)</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLORS.map((c) => (
                      <button
                        key={c.value}
                        onClick={() => setFarbe(c.value)}
                        className={`w-8 h-8 rounded-full ${c.bg} border-2 transition-all ${
                          farbe === c.value ? 'border-[hsl(var(--foreground))] scale-110' : 'border-transparent hover:scale-105'
                        }`}
                        title={c.label}
                        data-testid={`color-${c.value || 'auto'}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
                )}

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  disabled={(!firmenname && !werbetext) || generating}
                  className="w-full"
                  size="lg"
                  data-testid="generate-mockup-button"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      KI generiert Ihr Mockup...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Mockup generieren
                    </>
                  )}
                </Button>

                {generating && (
                  <p className="text-xs text-center text-[hsl(var(--muted-foreground))]">
                    Dauert ca. 15–30 Sekunden
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Right: Result */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <Card className="card-shadow h-full">
              <CardContent className="p-6 flex flex-col h-full">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-[hsl(var(--primary))]" />
                  Ihr Ergebnis
                </h3>

                <AnimatePresence mode="wait">
                  {resultUrl ? (
                    <motion.div
                      key="result"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex-1 flex flex-col"
                    >
                      <div className="rounded-xl overflow-hidden card-shadow mb-4" data-testid="mockup-result-image">
                        <img src={resultUrl} alt="Ihr Werbe-Mockup" className="w-full h-auto" />
                      </div>

                      <div className="flex gap-2 mb-4">
                        <Button onClick={handleDownload} variant="outline" className="flex-1" data-testid="download-mockup-button">
                          <Download className="w-4 h-4 mr-2" />
                          Herunterladen
                        </Button>
                        <Link to="/locations" className="flex-1">
                          <Button className="w-full" data-testid="book-after-mockup-button">
                            Jetzt buchen
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>

                      {/* Lead Capture */}
                      {!leadSaved ? (
                        <div className="p-4 bg-[hsl(var(--surface-2))] rounded-xl">
                          <p className="text-sm font-medium mb-2">Mockup per E-Mail erhalten?</p>
                          <div className="flex gap-2">
                            <Input
                              type="email"
                              placeholder="Ihre E-Mail-Adresse"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="flex-1"
                              data-testid="lead-email-input"
                            />
                            <Button onClick={handleSaveLead} size="sm" disabled={!email.includes('@')} data-testid="save-lead-button">
                              Senden
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-emerald-50 text-emerald-700 rounded-xl flex items-center gap-2" data-testid="lead-saved-confirmation">
                          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                          <span className="text-sm">Vielen Dank! Wir melden uns bei Ihnen.</span>
                        </div>
                      )}
                    </motion.div>
                  ) : generating ? (
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 flex flex-col items-center justify-center text-center py-12">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full bg-[hsl(var(--primary)/0.1)] flex items-center justify-center mb-4">
                          <Loader2 className="w-10 h-10 text-[hsl(var(--primary))] animate-spin" />
                        </div>
                      </div>
                      <p className="font-semibold mb-1">KI generiert Ihr Mockup...</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Wir erstellen eine realistische Vorschau Ihrer Werbung auf einem Anhänger.</p>
                    </motion.div>
                  ) : (
                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 flex flex-col items-center justify-center text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-[hsl(var(--surface-2))] flex items-center justify-center mb-4">
                        <ImageIcon className="w-10 h-10 text-[hsl(var(--muted-foreground))] opacity-40" />
                      </div>
                      <p className="font-medium text-[hsl(var(--muted-foreground))] mb-1">Noch kein Mockup erstellt</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Laden Sie Ihr Logo hoch und klicken Sie auf "Mockup generieren"</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Bottom info */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mt-12">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            {['Kostenlos & unverbindlich', 'Keine Registrierung nötig', 'Ergebnis in Sekunden'].map((t, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-[hsl(var(--chart-2))]" />
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MockupGeneratorPage;
